import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { browserHistory } from 'react-router';
import { Tooltip } from 'antd';
import {
	ApartmentOutlined,
	CaretDownFilled,
	CaretUpFilled,
} from '@ant-design/icons';

import ICONS from 'config/icons/dark';
import STRINGS from 'config/localizedStrings';
import Dialog from 'components/Dialog';
import HelpfulResourcesForm from 'containers/HelpfulResourcesForm';
import withConfig from 'components/ConfigProvider/withConfig';
import { Image, EditWrapper } from 'components';
import {
	isLoggedIn,
	removeToken,
	getMainAccountToken,
	setToken,
	checkAccountStatus,
} from 'utils/token';
import {
	setIsMarketDropdownVisible,
	setIsToolsVisible,
	setSecurityTab,
	setSettingsTab,
} from 'actions/appActions';
import { renderConfirmSignout } from './Utils';
import { logout } from 'actions/authAction';

const AccountTab = ({
	config_level,
	verification_level,
	user,
	icons: Icons,
	securityPending,
	verificationPending,
	setSecurityTab,
	setSettingsTab,
	setIsMarketDropdownVisible,
	setIsToolsVisible,
	features,
	logout,
}) => {
	const [isIconActive, setIsIconActive] = useState(false);
	const [isToolTipVisible, setIsToolTipVisible] = useState(false);

	const totalPending = securityPending + verificationPending;

	const onHandleRedirect = (path = '/') => {
		setIsToolTipVisible(false);
		setIsIconActive(false);
		browserHistory.push(path);
	};

	const onHandleVisible = () => {
		setIsIconActive(!isIconActive);
		setIsToolTipVisible(!isToolTipVisible);
		setIsMarketDropdownVisible(false);
		setIsToolsVisible(false);
	};

	return (
		<Tooltip
			visible={isToolTipVisible}
			title={
				<AccountList
					config_level={config_level}
					verification_level={verification_level}
					user={user}
					Icons={Icons}
					securityPending={securityPending}
					verificationPending={verificationPending}
					setIsToolTipVisible={setIsToolTipVisible}
					setIsIconActive={setIsIconActive}
					setSecurityTab={setSecurityTab}
					setSettingsTab={setSettingsTab}
					onHandleRedirect={onHandleRedirect}
					features={features}
					logout={logout}
				/>
			}
			placement="bottomRight"
			overlayClassName="navigation-bar-wrapper account-tab-dropdown"
			onVisibleChange={() => onHandleVisible()}
			mouseEnterDelay={0}
		>
			<div
				className={
					isToolTipVisible
						? 'd-flex app-bar-account-content active-text'
						: 'd-flex app-bar-account-content'
				}
				onClick={() => onHandleRedirect('/summary')}
			>
				<div className="mr-4">
					<Image
						iconId="SIDEBAR_ACCOUNT_INACTIVE"
						icon={ICONS['SIDEBAR_ACCOUNT_INACTIVE']}
						wrapperClassName="app-bar-account-icon"
					/>
					{!!totalPending && (
						<div className="app-bar-account-notification">{totalPending}</div>
					)}
				</div>
				<EditWrapper stringId="ACCOUNT_TEXT">
					<span className="account-title">{STRINGS['ACCOUNT_TEXT']}</span>
				</EditWrapper>
				<span className="ml-1 app-bar-dropdown-icon">
					{!isIconActive ? <CaretDownFilled /> : <CaretUpFilled />}
				</span>
			</div>
		</Tooltip>
	);
};

const AccountList = ({
	config_level,
	verification_level,
	user,
	Icons,
	securityPending,
	verificationPending,
	setIsIconActive,
	setIsToolTipVisible,
	setSecurityTab,
	setSettingsTab,
	onHandleRedirect,
	features,
	logout,
}) => {
	const [isHelpResources, setIsHelpResources] = useState(false);
	const [currPath, setCurrpath] = useState('/summary');
	const [isLogout, setIsLogout] = useState(false);

	useEffect(() => {
		const getCurrPage = window.location.pathname;
		setCurrpath(getCurrPage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.location.pathname]);

	const accountOptions = [
		{
			icon: 'WALLET_OPTION_ICON',
			title: 'ACCOUNTS.TAB_WALLET',
			description: 'DESKTOP_NAVIGATION.WALLET_DESCRIPTION',
			path: '/wallet',
			isDisplay: true,
		},
		{
			icon: 'OPTION_2FA_ICON',
			title: 'ACCOUNTS.TAB_SECURITY',
			description: 'DESKTOP_NAVIGATION.SECURITY_DESC',
			path: '/security',
			isDisplay: true,
		},
		{
			icon: 'IDENTITY_OPTION_ICON',
			title: 'ACCOUNTS.TAB_VERIFICATION',
			description: 'DESKTOP_NAVIGATION.VERIFICATION_DESC',
			path: '/verification',
			isDisplay: true,
		},
		{
			icon: 'PASSWORD_OPTION_ICON',
			title: 'ACCOUNTS.TAB_SETTINGS',
			description: 'DESKTOP_NAVIGATION.SETTINGS_DESC',
			path: '/settings',
			isDisplay: true,
		},
		{
			icon: 'ANNOUNCEMENT_ICON',
			title: 'TRADE_TAB_POSTS',
			description: 'DESKTOP_NAVIGATION.ANNOUNCEMENT_DESC',
			path: '/announcement',
			isDisplay: features?.announcement,
		},
	];

	const optionsRoute = [
		{
			icon: 'REVOKE_SESSION',
			title: 'SIGN_OUT_TEXT',
			toolTipText: 'DESKTOP_NAVIGATION.SIGNOUT_DESC',
			path: browserHistory.getCurrentLocation().pathname,
		},
		{
			icon: 'API_OPTION_ICON',
			title: 'MORE_OPTIONS_LABEL.ICONS.API',
			toolTipText: 'DESKTOP_NAVIGATION.API_DESC',
			path: '/security?apiKeys',
		},
		{
			icon: 'LANGUAGE_OPTION_ICON',
			title: 'USER_SETTINGS.TITLE_LANGUAGE',
			toolTipText: 'DESKTOP_NAVIGATION.LANGUAGE_DESC',
			path: '/settings?language',
		},
		{
			icon: 'HELP_OPTION_ICON',
			title: 'LOGIN.HELP',
			toolTipText: 'DESKTOP_NAVIGATION.HELP_DESC',
			path: browserHistory?.getCurrentLocation(),
		},
		{
			icon: 'HISTORY_OPTION_ICON',
			title: 'ACCOUNTS.TAB_HISTORY',
			toolTipText: 'DESKTOP_NAVIGATION.TRANSACTION_DESC',
			path: '/transactions',
		},
	];

	const renderHelpResource = () => {
		return (
			<Dialog
				isOpen={isHelpResources}
				onCloseDialog={() => setIsHelpResources(false)}
				label="helpful-resources"
			>
				<HelpfulResourcesForm
					onSubmitSuccess={() => setIsHelpResources(false)}
					onClose={() => setIsHelpResources(false)}
				/>
			</Dialog>
		);
	};

	const onHandleRoutes = (value = '/', title = '') => {
		const selectedTab = {
			'LOGIN.HELP': () => setIsHelpResources(true),
			SIGN_OUT_TEXT: () => setIsLogout(true),
			'ACCOUNTS.TAB_SECURITY': () => setSecurityTab(0),
			'MORE_OPTIONS_LABEL.ICONS.API': () => setSecurityTab(2),
			'USER_SETTINGS.TITLE_LANGUAGE': () => setSettingsTab(2),
			'ACCOUNTS.TAB_SETTINGS': () => setSettingsTab(0),
		};

		if (selectedTab[title]) {
			selectedTab[title]();
		}

		setIsToolTipVisible(false);
		setIsIconActive(false);

		return browserHistory?.push(value);
	};

	const userData =
		config_level[
			isLoggedIn() ? verification_level : Object.keys(config_level)[0]
		] || {};
	const userAccountTitle =
		userData.name ||
		STRINGS.formatString(
			STRINGS['SUMMARY.LEVEL_OF_ACCOUNT'],
			verification_level
		);
	const icon = Icons[`LEVEL_ACCOUNT_ICON_${verification_level}`]
		? Icons[`LEVEL_ACCOUNT_ICON_${verification_level}`]
		: Icons['LEVEL_ACCOUNT_ICON_4'];

	const onHandlelogout = () => {
		setIsLogout(false);
		removeToken();
		logout();
	};

	const onHandleclose = () => {
		setIsLogout(false);
	};

	const handleSwitchToMainAccount = () => {
		const mainAccountToken = getMainAccountToken();
		if (mainAccountToken) {
			setIsToolTipVisible(false);
			setIsIconActive(false);
			setToken(mainAccountToken);
			window.location.reload();
		}
	};

	const handleManageSubAccount = () => {
		setIsToolTipVisible(false);
		setIsIconActive(false);
		browserHistory.push('/settings?account');
	};

	return (
		<div className="navigation-dropdown-container">
			{isHelpResources && renderHelpResource()}
			<div className="tier-wrapper">
				<div className="d-flex align-items-center gap-1">
					<Image
						iconId={
							Icons[`LEVEL_ACCOUNT_ICON_${verification_level}`]
								? `LEVEL_ACCOUNT_ICON_${verification_level}`
								: 'LEVEL_ACCOUNT_ICON_4'
						}
						icon={icon}
						wrapperClassName={'trader-wrapper-icon trader-acc-detail-icon'}
					/>
					<span
						className="blue-link text-decoration-underline"
						onClick={() => onHandleRedirect('/fees-and-limits')}
					>
						{userAccountTitle}
					</span>
					<div className="d-flex flex-column align-items-start">
						<span className="secondary-text">({user?.email}) </span>
					</div>
				</div>
				<div className="d-flex gap-1 align-items-center">
					<ApartmentOutlined className="manage-account-icon" />
					<div className="d-flex align-items-center gap-1 mt-1">
						{user?.is_subaccount ? (
							<EditWrapper stringId="SUB_ACCOUNT_SYSTEM.SUB_ACCOUNT">
								<span className="secondary-text">
									{STRINGS['SUB_ACCOUNT_SYSTEM.SUB_ACCOUNT']}
								</span>
							</EditWrapper>
						) : (
							<EditWrapper stringId="SUB_ACCOUNT_SYSTEM.MAIN_ACCOUNT">
								<span className="secondary-text">
									{STRINGS['SUB_ACCOUNT_SYSTEM.MAIN_ACCOUNT']}
								</span>
							</EditWrapper>
						)}
					</div>
					<div>
						{checkAccountStatus('is_subaccount') ? (
							<EditWrapper stringId="SUB_ACCOUNT_SYSTEM.SWITCH_TO_MAIN_ACCOUNT">
								<span
									className="blue-link text-decoration-underline mt-2 pointer"
									onClick={handleSwitchToMainAccount}
								>
									{STRINGS['SUB_ACCOUNT_SYSTEM.SWITCH_TO_MAIN_ACCOUNT']}
								</span>
							</EditWrapper>
						) : (
							<EditWrapper stringId="SUB_ACCOUNT_SYSTEM.MANAGE_SUB_ACCOUNT">
								<span
									className="blue-link text-decoration-underline mt-2 pointer"
									onClick={handleManageSubAccount}
								>
									{STRINGS['SUB_ACCOUNT_SYSTEM.MANAGE_SUB_ACCOUNT']}
								</span>
							</EditWrapper>
						)}
					</div>
				</div>
			</div>
			{isLogout &&
				renderConfirmSignout(isLogout, onHandleclose, onHandlelogout)}
			{accountOptions?.map((options, index) => {
				return (
					options?.isDisplay && (
						<div
							key={index}
							className={
								currPath === options?.path
									? 'options-container account-active main-active'
									: 'options-container account-active'
							}
							onClick={() => onHandleRoutes(options?.path, options?.title)}
						>
							<Image
								icon={ICONS[options?.icon ? options?.icon : 'NO_ICON']}
								wrapperClassName="options-icon"
							/>
							<div className="option-details">
								<div className="d-flex">
									<EditWrapper stringId={options?.title}>
										<span>{STRINGS[options?.title]}</span>
									</EditWrapper>
									{!!securityPending &&
										options?.title === 'ACCOUNTS.TAB_SECURITY' && (
											<div className="app-bar-account-notification ml-2">
												{securityPending}
											</div>
										)}
									{!!verificationPending &&
										options?.title === 'ACCOUNTS.TAB_VERIFICATION' && (
											<div className="app-bar-account-notification ml-2">
												{verificationPending}
											</div>
										)}
								</div>
								<EditWrapper stringId={options?.description}>
									<span className="secondary-text">
										{STRINGS[options?.description]}
									</span>
								</EditWrapper>
							</div>
						</div>
					)
				);
			})}
			<div className="options-route-wrapper">
				{optionsRoute?.map((option, index) => {
					return (
						<Tooltip
							title={STRINGS[option?.toolTipText]}
							placement="topLeft"
							overlayClassName="account-tab-options-tooltip"
							key={index}
						>
							<div
								className="icon-option-container"
								onClick={() => onHandleRoutes(option?.path, option?.title)}
							>
								<Image icon={ICONS[option?.icon ? option?.icon : 'NO_ICON']} />
								<span className="text-nowrap">
									<EditWrapper>{STRINGS[option?.title]}</EditWrapper>
								</span>
							</div>
						</Tooltip>
					);
				})}
			</div>
			<div
				className="summary-page-link"
				onClick={() => onHandleRedirect('/summary')}
			>
				<EditWrapper stringId="DESKTOP_NAVIGATION.SUMMARY_PAGE">
					<span className="blue-link text-decoration-underline">
						{STRINGS['DESKTOP_NAVIGATION.SUMMARY_PAGE']}
					</span>
				</EditWrapper>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	config_level: state.app.config_level,
	verification_level: state.user.verification_level,
	features: state.app.features,
});

const mapDispatchToProps = (dispatch) => ({
	setSecurityTab: bindActionCreators(setSecurityTab, dispatch),
	setSettingsTab: bindActionCreators(setSettingsTab, dispatch),
	setIsMarketDropdownVisible: bindActionCreators(
		setIsMarketDropdownVisible,
		dispatch
	),
	setIsToolsVisible: bindActionCreators(setIsToolsVisible, dispatch),
	logout: bindActionCreators(logout, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withConfig(AccountTab));
